import axios from 'axios';

class Discover {

    constructor(options) {
        const defaults = {
            containerSelector: '#article-recommendations',
            listSelector: '.recommendations-list',
            templateSelector: '#recommendation-item-template',
            headerTemplateSelector: '',
            headerTextSelector: '[data-recommendation-header-text]',
            headerText: '',
            itemClass: 'recommendation-item',
            linkSelector: '.recommendation-link',
            titleSelector: '.recommendation-title',
            categorySelector: '.recommendation-category',
            publishedAtSelector: '.recommendation-published-at',
            dateLocale: 'el-GR',
            dateTimeZone: 'Europe/Athens',
            dateFormat: '',
            imageSelector: '.recommendation-image, .item-media img',
            userInterestClass: 'is-user-interest',
            loadingClass: 'loading',
            loadedClass: 'loaded',
            hiddenClass: 'hidden',
            apiBaseUrl: '',
            recommendationsPath: '/recommendations',
            eventsPath: '/events',
            placement: 'article_end',
            currentArticleId: '',
            algorithmProfile: '',
            maxCandidateAgeDays: null,
            rolloutPercent: 100,
            rolloutKey: '',
            limit: 6,
            sessionStorageKey: 'onsDiscoverSessionId',
            anonymousStorageKey: 'onsDiscoverAnonymousId',
            consentPersonalization: false,
            openLinksInNewTab: false,
            gaTracking: true,
            gaEventName: 'recommendation_click',
            noItemsMessage: ''
        };
        this.params = Object.assign({}, defaults, options || {});
        this.container = document.querySelector(this.params.containerSelector);
        this.list = this.container ? this.container.querySelector(this.params.listSelector) : null;
        this.template = document.querySelector(this.params.templateSelector);
        this.response = null;
        this.impressionTracked = false;
        this.renderedHeaderNodes = [];
        this.start();
    }

    async start() {
        if (!this.container) {
            return;
        }

        if (!this.template) {
            this.hide();
            return;
        }

        this.currentArticleId = this.resolveCurrentArticleId();
        this.sessionId = this.getOrCreateId(this.params.sessionStorageKey, window.sessionStorage);
        this.anonymousId = this.getOrCreateId(this.params.anonymousStorageKey, window.localStorage);
        this.isWidgetEnabled = this.isInRollout();

        if (!this.isWidgetEnabled) {
            this.hide();
            return;
        }

        if (!this.currentArticleId && this.params.placement === 'article_end') {
            this.hide();
            return;
        }

        this.container.classList.add(this.params.loadingClass);

        await this.fetchRecommendations();
        this.render();
    }

    resolveCurrentArticleId() {
        if (this.params.currentArticleId) {
            return String(this.params.currentArticleId);
        }

        if (this.container.dataset.articleId) {
            return String(this.container.dataset.articleId);
        }

        const meta = document.querySelector('meta[name="newsbomb:article-id"]');
        return meta ? meta.getAttribute('content') : '';
    }

    getOrCreateId(key, storage) {
        try {
            const existing = storage.getItem(key);
            if (existing) {
                return existing;
            }

            const id = this.createId();
            storage.setItem(key, id);
            return id;
        } catch (error) {
            return this.createId();
        }
    }

    createId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }

        return 'rec_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
    }

    isInRollout() {
        const rolloutPercent = Math.max(
            0,
            Math.min(100, Number(this.params.rolloutPercent))
        );
        const rolloutKey = this.params.rolloutKey
            || 'discover-rollout-v1';
        const bucket = this.stableBucket(rolloutKey + ':' + this.anonymousId);
        return bucket < rolloutPercent;
    }

    stableBucket(value) {
        let hash = 2166136261;
        const input = String(value);
        for (let index = 0; index < input.length; index++) {
            hash ^= input.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return (hash >>> 0) % 100;
    }

    endpoint(path) {
        return String(this.params.apiBaseUrl).replace(/\/$/, '') + path;
    }

    async fetchRecommendations() {
        try {
            const response = await axios.post(this.endpoint(this.params.recommendationsPath), {
                placement: this.params.placement,
                current_article_id: this.currentArticleId || null,
                anonymous_id: this.anonymousId,
                session_id: this.sessionId,
                algorithm_profile: this.params.algorithmProfile || null,
                max_candidate_age_days: this.params.maxCandidateAgeDays || null,
                limit: this.params.limit,
                consent_personalization: Boolean(this.params.consentPersonalization),
                context: {
                    page_url: window.location.href,
                    referrer: document.referrer || null
                }
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            this.response = response.data;
        } catch (error) {
            console.log(error);
            this.hide();
        }
    }

    render() {
        this.container.classList.remove(this.params.loadingClass);

        if (!this.response || !this.response.items || this.response.items.length === 0) {
            if (this.params.noItemsMessage) {
                this.container.textContent = this.params.noItemsMessage;
                return;
            }
            this.hide();
            return;
        }

        const target = this.list || this.container;
        target.innerHTML = '';
        this.renderHeader(target);

        for (let index = 0; index < this.response.items.length; index++) {
            target.appendChild(this.renderItem(this.response.items[index], index + 1));
        }

        this.container.classList.add(this.params.loadedClass);
        this.trackImpressionWhenVisible();
        this.bindClicks();
    }

    renderHeader(target) {
        this.removeRenderedHeader();

        if (!this.params.headerTemplateSelector) {
            return;
        }

        const template = document.querySelector(this.params.headerTemplateSelector);
        if (!template || !template.content) {
            return;
        }

        const header = template.content.cloneNode(true);
        const text = header.querySelector(this.params.headerTextSelector);
        if (text) {
            text.textContent = this.params.headerText;
        }

        this.renderedHeaderNodes = Array.from(header.childNodes);
        if (this.list) {
            target.before(header);
            return;
        }

        target.prepend(header);
    }

    removeRenderedHeader() {
        this.renderedHeaderNodes.forEach((node) => node.remove());
        this.renderedHeaderNodes = [];
    }

    renderItem(item, position) {
        const clone = this.template.content.cloneNode(true);
        const article = clone.firstElementChild || document.createElement('article');
        article.classList.add(this.params.itemClass);
        article.dataset.articleId = item.article_id;
        this.applyRecommendationReasonClass(article, item);

        const link = article.matches(this.params.linkSelector)
            ? article
            : article.querySelector(this.params.linkSelector) || article.querySelector('a');

        if (link) {
            link.href = item.url || '#';
            link.dataset.articleId = item.article_id;
            link.dataset.recommendationPosition = String(position);

            if (this.params.openLinksInNewTab) {
                link.target = '_blank';
                link.rel = 'noopener';
            }
        }

        this.setTitle(article, link, item.title || '');

        const category = item.metadata && item.metadata.category ? item.metadata.category : null;
        this.setText(article, this.params.categorySelector, category || '');

        if (item.published_at) {
            this.setText(
                article,
                this.params.publishedAtSelector,
                this.formatPublishedAt(item.published_at)
            );
        }

        const image = item.metadata && item.metadata.image ? item.metadata.image : null;
        this.setImage(article, image);

        return article;
    }

    formatPublishedAt(value) {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        if (this.params.dateFormat) {
            const parts = new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23',
                timeZone: this.params.dateTimeZone
            }).formatToParts(date);
            const values = {};
            parts.forEach((part) => {
                values[part.type] = part.value;
            });
            const tokens = {
                YYYY: values.year,
                MM: values.month,
                DD: values.day,
                HH: values.hour,
                mm: values.minute,
                ss: values.second
            };
            return String(this.params.dateFormat).replace(
                /YYYY|MM|DD|HH|mm|ss/g,
                (token) => tokens[token]
            );
        }

        return new Intl.DateTimeFormat(this.params.dateLocale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: this.params.dateTimeZone
        }).format(date).replace(' στις ', ', ');
    }

    applyRecommendationReasonClass(article, item) {
        const ranker = item.metadata && item.metadata.ranker ? item.metadata.ranker : null;
        if (!ranker) {
            return;
        }

        if (ranker.primary_signal === 'user_interest' || ranker.user_interest_lift === true) {
            article.classList.add(this.params.userInterestClass);
        }
    }

    setTitle(article, link, value) {
        if (link) {
            link.textContent = value;
            return;
        }

        this.setText(article, this.params.titleSelector, value);
    }

    setText(article, selector, value) {
        const element = article.querySelector(selector);
        if (!element) {
            return;
        }

        element.textContent = value;

        if (!value) {
            element.hidden = true;
        }
    }

    setImage(article, value) {
        const image = article.querySelector(this.params.imageSelector);
        if (!image) {
            return;
        }

        if (!value) {
            image.hidden = true;
            return;
        }

        image.src = value;
    }

    bindClicks() {
        this.container.addEventListener('click', (event) => {
            const link = event.target.closest(this.params.linkSelector);
            if (!link || !this.container.contains(link)) {
                return;
            }

            const metadata = {
                target_article_id: link.dataset.articleId,
                recommendation_position: Number(link.dataset.recommendationPosition) || null
            };

            this.trackEvent('recommendation_clicked', metadata);
            this.trackGoogleAnalyticsClick(link, metadata, event);
        });
    }

    trackGoogleAnalyticsClick(link, metadata, event) {
        if (!this.params.gaTracking || typeof window.gtag !== 'function' || !this.response) {
            return;
        }

        const gaMetadata = {
            recommendation_id: this.response.recommendation_id,
            placement: this.response.placement || this.params.placement,
            source_article_id: this.currentArticleId || undefined,
            target_article_id: metadata.target_article_id || undefined,
            recommendation_position: metadata.recommendation_position || undefined,
            algorithm_version: this.response.algorithm_version || undefined
        };
        const isSameTabNavigation = event.button === 0
            && !event.defaultPrevented
            && !event.metaKey
            && !event.ctrlKey
            && !event.shiftKey
            && !event.altKey
            && link.target !== '_blank'
            && link.href;

        if (!isSameTabNavigation) {
            window.gtag('event', this.params.gaEventName, gaMetadata);
            return;
        }

        // Hold a normal navigation briefly so GA4 receives the click event. The
        // destination page's standard GA4 page_view is then the resulting view.
        event.preventDefault();
        let navigated = false;
        const navigate = () => {
            if (navigated) {
                return;
            }
            navigated = true;
            window.location.assign(link.href);
        };

        window.gtag('event', this.params.gaEventName, Object.assign({}, gaMetadata, {
            event_callback: navigate,
            event_timeout: 500
        }));
        window.setTimeout(navigate, 600);
    }

    trackImpressionWhenVisible() {
        if (!('IntersectionObserver' in window)) {
            this.trackImpression();
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            for (let index = 0; index < entries.length; index++) {
                if (entries[index].isIntersecting) {
                    this.trackImpression();
                    observer.disconnect();
                    break;
                }
            }
        }, {
            threshold: 0.25
        });

        observer.observe(this.container);
    }

    trackImpression() {
        if (this.impressionTracked) {
            return;
        }

        this.impressionTracked = true;
        this.trackEvent('recommendation_shown', {
            recommended_article_ids: this.response.items.map((item) => item.article_id)
        });
    }

    async trackEvent(eventType, metadata) {
        if (!this.response) {
            return;
        }

        const payload = {
            event_type: eventType,
            recommendation_id: this.response.recommendation_id,
            article_id: this.currentArticleId || null,
            target_article_id: metadata.target_article_id || null,
            anonymous_id: this.anonymousId,
            session_id: this.sessionId,
            placement: this.response.placement || this.params.placement,
            metadata: Object.assign({
                algorithm_version: this.response.algorithm_version,
                fallback_used: this.response.fallback_used,
                cache_hit: this.response.cache_hit
            }, metadata || {})
        };
        const url = this.endpoint(this.params.eventsPath);

        if (window.fetch) {
            try {
                await window.fetch(url, {
                    method: 'POST',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                return;
            } catch (error) {
                console.log(error);
            }
        }

        try {
            await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            console.log(error);
        }
    }

    hide() {
        this.container.classList.remove(this.params.loadingClass);
        this.container.classList.add(this.params.hiddenClass);
    }
}

export {
    Discover as
        default
};
